import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import styles from './LoginPage.module.css'
import logo from '@/assets/logo.jpg'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await login(email, password)
      navigate('/dashboard')
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginContent}>
        {/* Left Side - Explainer */}
        <div className={styles.leftSide}>
          <Link to="/" className={styles.logoSection}>
            <img src={logo} alt="ShwetaHeals Logo" className={styles.logoImage} />
            <h1 className={styles.brandName}>ShwetaHeals</h1>
          </Link>

          <h2 className={styles.explainerTitle}>
            Your Mental Wellness Journey Starts Here
          </h2>
          <p className={styles.explainerText}>
            Join thousands who have found support, healing, and hope through our platform.
          </p>

          <div className={styles.features}>
            <div className={styles.feature}>
              <span className={styles.featureIcon}>🤖</span>
              <div className={styles.featureContent}>
                <h3 className={styles.featureTitle}>24/7 AI Support</h3>
                <p className={styles.featureDescription}>
                  Get compassionate support anytime you need it
                </p>
              </div>
            </div>

            <div className={styles.feature}>
              <span className={styles.featureIcon}>🔒</span>
              <div className={styles.featureContent}>
                <h3 className={styles.featureTitle}>100% Confidential</h3>
                <p className={styles.featureDescription}>
                  Your privacy is protected with end-to-end encryption
                </p>
              </div>
            </div>

            <div className={styles.feature}>
              <span className={styles.featureIcon}>👨‍⚕️</span>
              <div className={styles.featureContent}>
                <h3 className={styles.featureTitle}>Licensed Professionals</h3>
                <p className={styles.featureDescription}>
                  Connect with verified mental health specialists
                </p>
              </div>
            </div>
          </div>

          <div className={styles.trustBadges}>
            <div className={styles.badge}>
              <span className={styles.badgeIcon}>🛡️</span>
              <span className={styles.badgeText}>HIPAA Compliant</span>
            </div>
            <div className={styles.badge}>
              <span className={styles.badgeIcon}>✅</span>
              <span className={styles.badgeText}>Evidence-Based</span>
            </div>
            <div className={styles.badge}>
              <span className={styles.badgeIcon}>⭐</span>
              <span className={styles.badgeText}>Trusted by 1000+</span>
            </div>
          </div>

          <div className={styles.testimonial}>
            <p className={styles.testimonialText}>
              "This platform gave me the courage to seek help. The AI was so understanding,
              and connecting with a therapist was seamless."
            </p>
            <div className={styles.testimonialAuthor}>
              <strong>— Sarah M.</strong>, User since 2024
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className={styles.rightSide}>
          <div className={styles.loginCard}>
            <div className={styles.formHeader}>
              <h2 className={styles.title}>Welcome Back</h2>
              <p className={styles.subtitle}>Login to continue your journey</p>
            </div>

            <form onSubmit={handleSubmit} className={styles.form} autoComplete="on">
              {error && (
                <div className={styles.errorMessage}>
                  {error}
                </div>
              )}

              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className={styles.input}
                  placeholder="your.email@example.com"
                  autoComplete="email"
                  inputMode="email"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="password" className={styles.label}>
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  className={styles.input}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className={styles.submitButton}
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>

              <div className={styles.divider}>
                <span>or</span>
              </div>

              <div className={styles.registerPrompt}>
                Don't have an account?{' '}
                <Link to="/register" className={styles.registerLink}>
                  Create one here
                </Link>
              </div>

              <Link to="/" className={styles.backToHome}>
                ← Back to Home
              </Link>
            </form>
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        © 2025 Shweta Heals - Your Mental Health Matters
      </div>
    </div>
  )
}
